import { inject, Injectable, signal, ViewContainerRef } from '@angular/core';
import { Subject } from 'rxjs';
import { ISector } from '../../../shows/core/interfaces/ISector';
import { SeatsPlanStore } from '../../../../core/stores/SeatsPlanStore';
import { IElement } from '../interfaces/IElement';
import { ISeat } from '../interfaces/ISeat';
import { IShape } from '../interfaces/IShape';
import { IAutoSelectAnchorState } from '../interfaces/IAutoSelectAnchorState';
import { AutoSelectSeatsUtils } from '../utils/AutoSelectSeatsUtils';
import { SeatStatuses } from '../enums/SeatStatuses';

@Injectable({
  providedIn: 'root',
})
export class SeatsPlanService {

  private readonly _seatsPlanStore = inject(SeatsPlanStore);

  private _shapes = signal<IShape[]>([]);
  private _roomRef = signal<HTMLElement>(null);
  private _roomTemplate = signal<ViewContainerRef>(null);
  private _selectedSeats = signal<ISeat[]>([]);
  private _currentSector = signal<ISector>(null);
  private _baseElements = signal<IElement[]>([]);
  private _seats = signal<ISeat[]>([]);
  private _seatsRevision = signal(0);

  private _autoSelectShowId: number | null = null;
  private _anchorByPriceAreaId = new Map<number, IAutoSelectAnchorState>();
  private _lastAnchor: IAutoSelectAnchorState | null = null;
  private _pendingOptimisticSeatIds = new Set<number>();

  public readonly seatsOptimisticRevert$ = new Subject<number[]>();

  public roomRef = this._roomRef.asReadonly();
  public roomTemplate = this._roomTemplate.asReadonly();
  public shapes = this._shapes.asReadonly();
  public currentSector = this._currentSector;
  public baseElements = this._baseElements.asReadonly();
  public seats = this._seats.asReadonly();
  public seatsRevision = this._seatsRevision.asReadonly();
  public readonly seatsToAdd$ = new Subject<ISeat[]>();
  public readonly seatsToRemove$ = new Subject<ISeat[]>();
  public readonly seatsToReplace$ =
    new Subject<{ seatsToAdd: ISeat[]; seatsToRemove: ISeat[]; }>();
  public readonly seatBlockLongPress$ = new Subject<ISeat>();
  public readonly seatShortTouch$ = new Subject<ISeat>();

  public updateSelectedSeats({
    seatsToAdd,
    seatsToRemove
  }: {
    seatsToAdd: ISeat[],
    seatsToRemove: ISeat[]
  }): void {
    if (seatsToAdd.length > 0) {
      this.seatsToAdd$.next(seatsToAdd);
    } else if (seatsToRemove.length > 0) {
      this.seatsToRemove$.next(seatsToRemove);
    }

    this._selectedSeats.update(seats => {
      const updatedSeats = seats.filter(seat => !seatsToRemove.includes(seat));
      return [...updatedSeats, ...seatsToAdd.filter(seat => !updatedSeats.includes(seat))];
    });
  }

  public setShapes(shapes: IShape[]): void {
    this._shapes.set(shapes);
  }

  public setCurrentSector(sector: ISector): void {
    this._currentSector.set(sector);
    this._seatsPlanStore.rowNameOnLeft.set(!!sector.rowNameOnLeft);
  }

  public setBaseElements(baseElements: IElement[]): void {
    this._baseElements.set(baseElements);
  }

  public setSeats(seats: ISeat[]): void {
    this._seats.set(seats);
    this.notifySeatsMutated();
  }

  public notifySeatsMutated(): void {
    this._seatsRevision.update(revision => revision + 1);
  }

  public setRoomRef(roomRef: HTMLElement): void {
    this._roomRef.set(roomRef);
  }

  public setRoomTemplate(roomTemplate: ViewContainerRef): void {
    this._roomTemplate.set(roomTemplate);
  }

  public resetAutoSelectState(showId?: number): void {
    this._anchorByPriceAreaId.clear();
    this._lastAnchor = null;
    this._pendingOptimisticSeatIds.clear();

    if (showId !== undefined) {
      this._autoSelectShowId = showId;
    } else {
      this._autoSelectShowId = null;
    }
  }

  public syncAutoSelectShow(showId: number): void {
    if (this._autoSelectShowId !== showId) {
      this.resetAutoSelectState(showId);
    }
  }


  public getAnchorForPriceArea(priceAreaId: number): IAutoSelectAnchorState | null {
    const zoneAnchor = this._anchorByPriceAreaId.get(priceAreaId);

    if (zoneAnchor) {
      return zoneAnchor;
    }

    return this._lastAnchor;
  }

  public acknowledgeOptimisticPriceRowSeats(seatIds: readonly number[]): void {
    for (const seatId of seatIds) {
      this._pendingOptimisticSeatIds.delete(seatId);
    }
  }

  public revertOptimisticPriceRowSeats(seatIds: readonly number[]): void {
    const idsToRevert = seatIds.filter(seatId => this._pendingOptimisticSeatIds.has(seatId));

    if (idsToRevert.length === 0) {
      return;
    }

    const revertSet = new Set(idsToRevert);

    this._seats.update(seats =>
      seats.map(seat => {
        if (!revertSet.has(seat.id)) {
          return seat;
        }

        return {
          ...seat,
          isMy: false,
          seatStatusKey: SeatStatuses.FREE,
          priceCategoryId: undefined,
        };
      })
    );

    for (const seatId of idsToRevert) {
      this._pendingOptimisticSeatIds.delete(seatId);
    }

    this.notifySeatsMutated();
    this.seatsOptimisticRevert$.next(idsToRevert);
  }

  public addSeatsFromPriceRow(
    priceCategoryId: number,
    priceAreaId: number,
    count: number,
    showId: number
  ): number[] {
    this.syncAutoSelectShow(showId);

    const anchor = this.getAnchorForPriceArea(priceAreaId);
    const result = AutoSelectSeatsUtils.selectSeats({
      seats: this.seats(),
      shapes: this.shapes(),
      priceAreaId,
      anchorRankToken: anchor?.anchorRankToken ?? null,
      count,
    });

    if (result.selectedSeatIds.length === 0) {
      return [];
    }

    const selectedIdSet = new Set(result.selectedSeatIds);
    const seatsToAdd: ISeat[] = [];

    const patchedSeats = this.seats().map(seat => {
      if (!selectedIdSet.has(seat.id)) {
        return seat;
      }

      const patched: ISeat = {
        ...seat,
        priceCategoryId,
        isMy: true,
        seatStatusKey: SeatStatuses.NOTED,
      };

      seatsToAdd.push(patched);
      this._pendingOptimisticSeatIds.add(seat.id);

      return patched;
    });

    this._seats.set(patchedSeats);
    this.notifySeatsMutated();

    if (result.anchorRankToken !== null) {
      const nextAnchor: IAutoSelectAnchorState = {
        anchorRankToken: result.anchorRankToken,
      };

      this._anchorByPriceAreaId.set(priceAreaId, nextAnchor);
      this._lastAnchor = nextAnchor;
    }

    this.seatsToAdd$.next(seatsToAdd);

    return result.selectedSeatIds;
  }
}
